<template>
  <div class="atm-wrapper">
    <!-- HEADER -->
    <div class="atm-header">
      <AllIssuesHeader
        @getSearch="getSearch"
        :filterData="filterData"
        @filterTaskData="getFilterData"
      />
    </div>

    <!-- LOADER -->
    <div class="atm-loader" v-if="loading">
      <div class="atm-shimmer-list">
        <div class="atm-shimmer-card" v-for="n in 5" :key="n">
          <div class="atm-shimmer atm-shimmer--avatar"></div>
          <div class="atm-shimmer-body">
            <div class="atm-shimmer-top">
              <div class="atm-shimmer atm-shimmer--ref"></div>
              <div class="atm-shimmer atm-shimmer--stage"></div>
            </div>
            <div class="atm-shimmer atm-shimmer--name"></div>
            <div class="atm-shimmer atm-shimmer--name atm-shimmer--name-short"></div>
            <div class="atm-shimmer-bottom">
              <div class="atm-shimmer atm-shimmer--chip"></div>
              <div class="atm-shimmer atm-shimmer--date"></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ISSUE LIST -->
    <div class="atm-list" v-else-if="issueType.length > 0">
      <div
        v-for="(item, index) in issueType"
        :key="index"
        class="atm-card"
        :style="{ animationDelay: index * 70 + 'ms' }"
        @click="goIssueView(item)"
      >
        <div class="atm-card__inner" :class="'atm-card__inner--' + (item.priority || 'normal')">
          <!-- Priority accent bar -->
          <div class="atm-card__bar" :style="priorityBarStyle(item.priority)"></div>

          <!-- LEFT: Avatar -->
          <div class="atm-card__avatar">
            <CustomAvatar2
              :acCode="item.account_code"
              :fName="item.account_name"
              :theme="item.account_theme"
              :imgUrl="item.a_image_url ? item.a_image_url : null"
              :size="'large'"
            />
          </div>

          <!-- BODY -->
          <div class="atm-card__body">
            <!-- TOP ROW: ref + priority + copy + stage -->
            <div class="atm-card__top">
              <div class="atm-card__ref-row">
                <span class="atm-ref" :style="priorityRefStyle(item.priority)">{{ item.reference_num }}</span>
                <!-- Priority badge -->
                <span class="atm-priority-badge" :style="priorityBadgeStyle(item.priority)" v-if="['critical','high','medium'].includes(item.priority)">
                  <v-icon size="10" v-if="item.priority === 'critical'">mdi-alert</v-icon>
                  <v-icon size="10" v-else-if="item.priority === 'high'">mdi-exclamation-thick</v-icon>
                  <v-icon size="10" v-else-if="item.priority === 'medium'">mdi-minus</v-icon>
                  <v-icon size="10" v-else-if="item.priority === 'low'">mdi-arrow-down</v-icon>
                </span>
                <v-icon
                  v-if="item.week_end_mails == 'yes'"
                  size="12"
                  color="#3b82f6"
                  title="Weekend mail"
                >mdi-circle-half-full</v-icon>
              </div>
              <div class="atm-card__actions">
                <v-btn
                  variant="text"
                  size="x-small"
                  icon
                  @click.stop="copyTextToClipboard(item.name)"
                  class="atm-copy-btn"
                  title="Copy issue name"
                >
                  <v-icon size="14">mdi-content-copy</v-icon>
                </v-btn>
                <span class="atm-stage-chip" v-if="item.stage_name" :style="stageChipStyle(item.stage_name)">
                  {{ item.stage_name }}
                </span>
              </div>
            </div>
            <!-- ISSUE NAME -->
            <div class="atm-card__name-row">
              <span class="atm-issue-name">{{ item.name }}</span>
            </div>
            <!-- BOTTOM ROW -->
            <div class="atm-card__bottom">
              <v-tooltip
                v-if="item.sub_category_name"
                location="top"
                :text="item.sub_category_name"
                open-delay="200"
              >
                <template #activator="{ props }">
                  <span class="atm-sub-chip" v-bind="props">
                    {{ item.sub_category_name.length > 20 ? item.sub_category_name.slice(0, 20) + '...' : item.sub_category_name }}
                  </span>
                </template>
              </v-tooltip>
              <div class="atm-date" :class="isDateOverDue(item.mail_date || item.created)">
                <span class="atm-date__dot atm-date__dot--overdue" v-if="isDateOverDue(item.mail_date || item.created) === 'atm-date--overdue'"></span>
                <span class="atm-date__dot atm-date__dot--pastdue" v-else-if="isDateOverDue(item.mail_date || item.created) === 'atm-date--upcoming'"></span>
                <v-icon size="11">mdi-calendar-outline</v-icon>
                <span>{{ moment(item.mail_date || item.created).format("MMM DD, YYYY") }}</span>
                <span class="atm-date__time">{{ moment(item.mail_date || item.created).format("ddd hh:mm A") }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- EMPTY STATE -->
    <div class="atm-empty" v-else>
      <div class="atm-empty__illustration">
        <img :src="require('@/assets/no-record.png')" width="90" height="90" />
      </div>
      <p class="atm-empty__title">No Assignments Found</p>
      <p class="atm-empty__sub">You're all caught up! No issues assigned to you.</p>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import StageService from "@/api/service/Stage/StageService";
import IssueService from "@/api/service/Issue/IssueService";
import DocumentService from "@/api/service/Document/DocumentService";
import AllIssuesHeader from "@/modules/dashboard/user/AllIssuesHeader.vue";
import CustomAvatar2 from "@/components/ui/CustomAvatar2.vue";
import lodash from "lodash";

export default {
  props: ["userId"],
  components: {
    AllIssuesHeader,
    CustomAvatar2,
  },
  data() {
    return {
      stageData: [],
      issueType: [],
      loading: false,
      originalIssueType: [],
      userData: JSON.parse(localStorage.getItem("user_details")),
    };
  },
  computed: {
    reloadIssue() {
      return this.$store.getters.getIssueUpdated;
    },
    reloadTheData() {
      return this.$store.getters.getReloadTheIssue;
    },
    getRefresh() {
      return this.$store.getters.getInProgress;
    },
    getTimerHide() {
      return this.$store.getters.getHideTimer;
    },
    getPending() {
      return this.$store.getters.getPending;
    },
    issueUpdated() {
      return this.$store.getters.getUpdatedIssueStatus;
    },
    issueCreated() {
      return this.$store.getters.getCreatedIssueStatus;
    },
  },
  watch: {
    reloadIssue() {
      if (this.$store.getters.getIssueUpdated != 0) {
        this.getUpdatedData(this.$store.getters.getIssueUpdated);
      }
    },
    issueCreated() {
      if (this.issueCreated) {
        this.getissuetype();
        this.$store.commit("SET_ISSUE_CREATED", false);
        this.getIssueById(this.$store.getters.getUpdatedIssueId, "new");
      }
    },
    issueUpdated() {
      if (this.issueUpdated) {
        this.getissuetype();
        this.$store.commit("SET_OLD_ISSUE_UPDATED", false);
        this.getIssueById(this.$store.getters.getUpdatedIssueId, "update");
      }
    },
    getTimerHide() {
      this.getissuetype();
    },
    reloadTheData() {
      this.getStageData();
      this.getissuetype();
    },
    getPending() {
      if (this.getPending) {
        this.getStageData();
        this.getissuetype();
      }
    },
    userId() {
      if (this.userId) this.getissuetype();
    },
    getRefresh() {
      if (this.getRefresh) {
        this.getStageData();
        this.getissuetype();
      }
    },
  },
  mounted() {
    this.getStageData();
    this.getissuetype();
  },
  methods: {
    async getIssueById($id, $type) {
      let $res = await IssueService.getAssignToMeData({ id: $id });
      if ($res && $res.length > 0) {
        if ($type == "new") {
          this.issueType.push($res[0]);
        } else {
          let $index = this.issueType.findIndex((k) => k.id == $res[0].id);
          if ($index > -1) this.issueType.splice($index, 1, $res[0]);
        }
      }
    },
    getSearch(query) {
      if (!this.originalIssueType.length) {
        this.originalIssueType = JSON.parse(JSON.stringify(this.issueType));
      }
      if (query && query.length > 0) {
        // Escape special regex chars so formats like SYNTST-38109/592 work correctly
        const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const searchStr = new RegExp(escaped, "gi");
        this.issueType = this.originalIssueType.filter(
          (item) =>
            (item.name && searchStr.test(item.name)) ||
            (item.reference_num && searchStr.test(item.reference_num)) ||
            (item.sub_category_name && searchStr.test(item.sub_category_name))
        );
      } else {
        this.issueType = JSON.parse(JSON.stringify(this.originalIssueType));
      }
    },
    isDateOverDue(date) {
      if (date) {
        return new Date() > new Date(date) ? "atm-date--overdue" : "atm-date--upcoming";
      }
    },
    moment,
    copyTextToClipboard(data) {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(data).catch((err) => console.error("Unable to copy text: ", err));
      }
    },
    async getStageData() {
      try {
        let res = await StageService.findAll();
        this.stageData = res.length > 0 ? res : [];
      } catch (e) {
        this.stageData = [];
      }
    },
    getStage(value) {
      let $stage = value.replaceAll(" ", "").toLowerCase();
      let opt = this.stageData.filter((k) => k.code == $stage);
      return opt && opt.length > 0 ? opt[0].name : "";
    },
    stageChipStyle(stageName) {
      const code = stageName ? stageName.replaceAll(' ', '').toLowerCase() : '';
      const map = {
        open:              { bg: '#eff6ff', color: '#2563eb', border: '#bfdbfe' },
        pending:           { bg: '#fefce8', color: '#ca8a04', border: '#fde68a' },
        nextaction:        { bg: '#f5f3ff', color: '#7c3aed', border: '#ddd6fe' },
        nextactiontibura:  { bg: '#f5f3ff', color: '#7c3aed', border: '#ddd6fe' },
        nextactioncf:      { bg: '#f5f3ff', color: '#7c3aed', border: '#ddd6fe' },
        inprogress:        { bg: '#eff6ff', color: '#1d4ed8', border: '#93c5fd' },
        qapending:         { bg: '#fff7ed', color: '#c2410c', border: '#fed7aa' },
        qacompleted:       { bg: '#f0fdf4', color: '#15803d', border: '#86efac' },
        closed:            { bg: '#f1f5f9', color: '#475569', border: '#cbd5e1' },
        itsupportrequired: { bg: '#fdf4ff', color: '#9333ea', border: '#e9d5ff' },
        completed:         { bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' },
        hold:              { bg: '#fef9c3', color: '#a16207', border: '#fde047' },
        cancel:            { bg: '#fff1f2', color: '#be123c', border: '#fecdd3' },
        clarification:     { bg: '#fff7ed', color: '#ea580c', border: '#fed7aa' },
        assigned:          { bg: '#ecfdf5', color: '#059669', border: '#6ee7b7' },
        receivedreply:     { bg: '#f0f9ff', color: '#0284c7', border: '#bae6fd' },
        errorlog:          { bg: '#fff1f2', color: '#dc2626', border: '#fca5a5' },
        workorder:         { bg: '#faf5ff', color: '#7e22ce', border: '#d8b4fe' },
        nsa:               { bg: '#fff7ed', color: '#b45309', border: '#fcd34d' },
        wfa:               { bg: '#eff6ff', color: '#3b82f6', border: '#93c5fd' },
        qcpass:            { bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' },
        qcfail:            { bg: '#fff1f2', color: '#dc2626', border: '#fca5a5' },
        pipend:            { bg: '#fefce8', color: '#ca8a04', border: '#fde68a' },
        tckpnd:            { bg: '#fdf4ff', color: '#9333ea', border: '#e9d5ff' },
        followup:          { bg: '#f0f9ff', color: '#0369a1', border: '#bae6fd' },
      };
      const s = map[code] || { bg: '#f8fafc', color: '#64748b', border: '#e2e8f0' };
      return { background: s.bg, color: s.color, borderColor: s.border };
    },
    getAvatarName(value) {
      return (value?.first_name?.charAt(0) || "") + (value?.last_name?.charAt(0) || "");
    },
    /* ── Priority colour map ── */
    priorityMap() {
      return {
        critical: { bg: '#fdf2f8', border: '#ff0000', bar: 'linear-gradient(180deg,#db2777,#9d174d)', refBg: '#fce7f3', refColor: '#ff0000', badgeBg: '#ff0000', badgeColor: '#fff' },
        high:     { bg: '#fff4ec', border: '#fdba74', bar: 'linear-gradient(180deg,#f97316,#c2410c)', refBg: '#ffedd5', refColor: '#c2410c', badgeBg: 'linear-gradient(135deg,#fb923c,#ea580c)', badgeColor: '#fff' },
        medium:   { bg: '#fefce8', border: '#fde68a', bar: 'linear-gradient(180deg,#eab308,#ca8a04)', refBg: '#fef9c3', refColor: '#a16207', badgeBg: 'linear-gradient(135deg,#facc15,#ca8a04)', badgeColor: '#fff' },
        low:      { bg: '#ffffff', border: '#e9edf5', bar: 'linear-gradient(180deg,#6366f1,#a78bfa,#34d399)', refBg: '#eef2ff', refColor: '#6366f1', badgeBg: null, badgeColor: null },
        normal:   { bg: '#ffffff', border: '#e9edf5', bar: 'linear-gradient(180deg,#6366f1,#a78bfa,#34d399)', refBg: '#eef2ff', refColor: '#6366f1', badgeBg: null, badgeColor: null },
      };
    },
    priorityBarStyle(priority) {
      const p = this.priorityMap()[priority] || this.priorityMap().normal;
      return { background: p.bar };
    },
    priorityRefStyle(priority) {
      const p = this.priorityMap()[priority] || this.priorityMap().normal;
      return { background: p.refBg, color: p.refColor };
    },
    priorityBadgeStyle(priority) {
      const p = this.priorityMap()[priority] || this.priorityMap().normal;
      return { background: p.badgeBg, color: p.badgeColor };
    },
    async getFilterData(data) {
      this.filterData = data;
      this.filterDialog = false;
      this.getissuetype(this.filterData);
    },
    goIssueView(item) {
      let status = this.$store.getters.getMyspace;
      localStorage.setItem("_WS_DEFAULTS_", JSON.stringify({
        workspace: { id: item.work_space_id, name: item.work_space_name },
      }));
      localStorage.setItem("workSpaceId", item.work_space_id);
      this.$store.dispatch("setIssueInView", item.id);
      let wsDefaults = {
        workspace: { id: item.work_space_id, name: item.work_space_name },
        account: { id: item.account_id, name: item.account_name },
        project: { id: item.project_id, name: item.project_name },
      };
      localStorage.setItem("_WS_DEFAULTS_", JSON.stringify(wsDefaults));
      this.$store.commit("SET_REFRESH_BREADCRUMP", true);
      this.$store.commit("CHANGE_MY_SPACE", !status);
    },
    async getImage(id) {
      try {
        let res = await DocumentService.imageProfileAccount({ image_profile_id: id });
        return res;
      } catch (e) {
        return null;
      }
    },
    async filteredData($data) {
      let $filteredRows = await lodash.orderBy($data, ["mail_date", "created"], ["asc"]);
      this.issueType = [...$filteredRows];
    },
    async getUpdatedData($id) {
      var res = await IssueService.getAssignToMeData({ id: $id });
      let $findIndex = lodash.findIndex(this.issueType, { id: $id });
      if ($findIndex >= 0) {
        this.issueType[$findIndex] = res[0];
      } else {
        this.issueType.push(res[0]);
      }
      this.$store.dispatch("issueUpdated", 0);
    },
    async getissuetype($obj) {
      this.loading = true;
      try {
        let obj = $obj ? { ...$obj } : {};
        obj.next_action = this.userId;
        var res = await IssueService.getAssignToMeData(obj);
        this.issueType = res && res.length ? res : [];
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
/* ── KEYFRAMES ── */
@keyframes atm-slide-in {
  from { opacity: 0; transform: translateY(20px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}
@keyframes atm-pulse-dot {
  0%, 100% { transform: scale(1); opacity: 1; box-shadow: 0 0 0 0 rgba(239,68,68,0.5); }
  50%       { transform: scale(1.4); opacity: 0.6; box-shadow: 0 0 0 5px rgba(239,68,68,0); }
}
@keyframes atm-border-spin {
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}
@keyframes atm-blink-critical {
  0%, 100% { box-shadow: 0 0 0 0 rgba(219,39,119,0.45); }
  50%       { box-shadow: 0 0 0 6px rgba(219,39,119,0); }
}
@keyframes atm-pulse-orange {
  0%, 100% { transform: scale(1); opacity: 1; box-shadow: 0 0 0 0 rgba(234,88,12,0.5); }
  50%       { transform: scale(1.4); opacity: 0.6; box-shadow: 0 0 0 5px rgba(234,88,12,0); }
}
@keyframes atm-bg-drift {
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

/* ── WRAPPER ── */
.atm-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: linear-gradient(135deg, #f8fafc 0%, #f0f4ff 50%, #f8fafc 100%);
  background-size: 300% 300%;
  animation: atm-bg-drift 12s ease infinite;
  border-radius: 16px;
  overflow: hidden;
}

/* ── HEADER ── */
.atm-header {
  background: rgba(255,255,255,0.85);
  backdrop-filter: blur(8px);
  position: sticky;
  top: 0;
  z-index: 5;
  border-bottom: 1px solid rgba(99,102,241,0.08);
}

/* ── LIST ── */
.atm-list {
  flex: 1;
  overflow-y: auto;
  padding: 12px 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* ── CARD ── */
.atm-card {
  display: flex;
  border-radius: 14px;
  cursor: pointer;
  position: relative;
  animation: atm-slide-in 0.42s cubic-bezier(0.22, 1, 0.36, 1) both;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.atm-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(99,102,241,0.12);
}

/* white fill inner wrapper — tinted per priority */
.atm-card__inner {
  display: flex;
  gap: 0;
  width: 100%;
  border-radius: 13px;
  position: relative;
  z-index: 1;
  overflow: hidden;
  border: 1.5px solid #e9edf5;
  background: #ffffff;
  transition: border-color 0.2s ease, background 0.2s ease;
}
.atm-card__inner--critical {
  background: #fdf2f8;
  border-color: #ff0000;
  box-shadow: 0 2px 14px rgba(219, 39, 39, 0.13);
  animation: atm-blink-critical 2s ease-in-out infinite;
}
.atm-card__inner--high {
  background: #fff4ec;
  border-color: #fdba74;
  box-shadow: 0 2px 14px rgba(249,115,22,0.12);
}
.atm-card__inner--medium {
  background: #fefce8;
  border-color: #fde68a;
  box-shadow: 0 2px 12px rgba(234,179,8,0.08);
}
.atm-card__inner--low,
.atm-card__inner--normal {
  background: #ffffff;
  border-color: #e9edf5;
}

/* left accent bar — color set inline */
.atm-card__bar {
  width: 4px;
  flex-shrink: 0;
  border-radius: 13px 0 0 13px;
}

/* Priority badge (replaces old .atm-priority) */
.atm-priority-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 9px;
  font-weight: 700;
  padding: 3px;
  border-radius: 20px;
  text-transform: capitalize;
  letter-spacing: 0.3px;
}

/* ── AVATAR ── */
.atm-card__avatar {
  flex-shrink: 0;
  padding: 14px 10px 14px 12px;
  display: flex;
  align-items: flex-start;
}

/* ── BODY ── */
.atm-card__body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 12px 12px 12px 0;
}

/* TOP ROW */
.atm-card__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
}
.atm-card__ref-row {
  display: flex;
  align-items: center;
  gap: 5px;
}
.atm-ref {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 9px;
  border-radius: 20px;
  letter-spacing: 0.4px;
  /* color & bg set inline via priorityRefStyle */
}



.atm-card__actions {
  display: flex;
  align-items: center;
  gap: 4px;
}
.atm-copy-btn { opacity: 1; }

.atm-stage-chip {
  font-size: 9.5px;
  font-weight: 600;
  padding: 2px 9px;
  border-radius: 20px;
  border: 1px solid;
  white-space: nowrap;
  text-transform: capitalize;
}

/* NAME ROW */
.atm-card__name-row { display: flex; align-items: flex-start; gap: 6px; }

.atm-issue-name {
  font-size: 12.5px;
  font-weight: 600;
  color: #1e293b;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  line-height: 1.5;
  flex: 1;
}

/* BOTTOM ROW */
.atm-card__bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding-top: 6px;
  border-top: 1px solid #f1f5f9;
  margin-top: 2px;
}

.atm-sub-chip {
  font-size: 9.5px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 20px;
  background: #fff7ed;
  color: #c2410c;
  border: 1px solid #fed7aa;
  white-space: nowrap;
  flex-shrink: 0;
  cursor: default;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.atm-date {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  font-weight: 600;
  white-space: nowrap;
  flex-shrink: 0;
  padding: 2px 8px;
  border-radius: 20px;
  background: #f8fafc;
}
.atm-date__time { font-weight: 400; opacity: 0.75; }
.atm-date--overdue  { color: #ef4444; background: #fff1f2; }
.atm-date--upcoming { color: #ea580c; background: #fff7ed; }

.atm-date__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}
.atm-date__dot--overdue {
  background: #ef4444;
  animation: atm-pulse-dot 1.4s ease-in-out infinite;
}
.atm-date__dot--pastdue {
  background: #ea580c;
  animation: atm-pulse-orange 1.4s ease-in-out infinite;
}

/* ── EMPTY STATE ── */
.atm-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  gap: 12px;
}
.atm-empty__illustration {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #f5f3ff, #ede9fe);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 4px;
}
.atm-empty__title { font-size: 14px; font-weight: 700; color: #1e293b; margin: 0; }
.atm-empty__sub   { font-size: 12px; color: #94a3b8; margin: 0; text-align: center; line-height: 1.6; }

/* ── LOADER ── */
.atm-loader {
  flex: 1;
  overflow-y: auto;
  padding: 12px 12px 16px;
}
.atm-shimmer-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.atm-shimmer-card {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  background: #ffffff;
  border-radius: 14px;
  border: 1.5px solid #e9edf5;
  padding: 14px 14px 12px 12px;
}
.atm-shimmer {
  border-radius: 6px;
  background: linear-gradient(90deg, #f1f5f9 25%, #e8edf5 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: atm-shimmer-move 1.4s ease-in-out infinite;
}
/* stagger delay per card */
.atm-shimmer-card:nth-child(1) .atm-shimmer { animation-delay: 0s; }
.atm-shimmer-card:nth-child(2) .atm-shimmer { animation-delay: 0.1s; }
.atm-shimmer-card:nth-child(3) .atm-shimmer { animation-delay: 0.2s; }
.atm-shimmer-card:nth-child(4) .atm-shimmer { animation-delay: 0.3s; }
.atm-shimmer-card:nth-child(5) .atm-shimmer { animation-delay: 0.4s; }

.atm-shimmer--avatar    { width: 40px; height: 40px; border-radius: 50%; flex-shrink: 0; }
.atm-shimmer-body       { flex: 1; display: flex; flex-direction: column; gap: 8px; min-width: 0; }
.atm-shimmer-top        { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
.atm-shimmer--ref       { height: 18px; width: 90px; border-radius: 20px; }
.atm-shimmer--stage     { height: 18px; width: 70px; border-radius: 20px; }
.atm-shimmer--name      { height: 12px; width: 100%; }
.atm-shimmer--name-short { width: 65%; }
.atm-shimmer-bottom     { display: flex; justify-content: space-between; align-items: center; gap: 8px; padding-top: 4px; }
.atm-shimmer--chip      { height: 18px; width: 80px; border-radius: 20px; }
.atm-shimmer--date      { height: 18px; width: 110px; border-radius: 20px; }
@keyframes atm-shimmer-move {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* ── SCROLLBAR ── */
::-webkit-scrollbar { width: 4px; }
::-webkit-scrollbar-track { background: transparent; }
::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 4px; }
::-webkit-scrollbar-thumb:hover { background: #cbd5e1; }

>>> .p-avatar.p-avatar-lg { width: 3rem; height: 3rem; font-size: 18px; }
</style>
